import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Info, ChevronLeft, ChevronRight, X, ExternalLink } from 'lucide-react';
import { getBarNotices } from '@/services/noticeService';
import { noticeTypeConfig, type Notice } from '@/types/notices';

const ROTATION_MS = 7000;
const DISMISS_STORAGE_KEY = 'starline_dismissed_notices';

function getDismissed(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_STORAGE_KEY) || '{}');
  } catch { return {}; }
}

function setDismissed(id: string, updatedAt: string) {
  const d = getDismissed();
  d[id] = updatedAt;
  localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(d));
}

function isDismissed(n: Notice): boolean {
  const d = getDismissed();
  if (!d[n.id]) return false;
  // Re-show if notice was updated after dismissal
  return d[n.id] === n.updated_at;
}

export default function AnnouncementBar() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  useEffect(() => {
    getBarNotices().then(data => {
      setNotices(data.filter(n => !isDismissed(n)));
    });
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (notices.length <= 1 || paused) return;
    const timer = setInterval(() => {
      setCurrent(c => (c + 1) % notices.length);
    }, ROTATION_MS);
    return () => clearInterval(timer);
  }, [notices.length, paused]);

  // Check if text needs scrolling
  useEffect(() => {
    const check = () => {
      if (textRef.current && containerRef.current) {
        setNeedsScroll(textRef.current.scrollWidth > containerRef.current.clientWidth);
      }
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [current, notices]);

  const handleDismiss = useCallback((notice: Notice) => {
    setDismissed(notice.id, notice.updated_at);
    setNotices(prev => {
      const next = prev.filter(n => n.id !== notice.id);
      if (current >= next.length && next.length > 0) setCurrent(next.length - 1);
      return next;
    });
  }, [current]);

  const goPrev = useCallback(() => {
    setCurrent(c => (c - 1 + notices.length) % notices.length);
  }, [notices.length]);

  const goNext = useCallback(() => {
    setCurrent(c => (c + 1) % notices.length);
  }, [notices.length]);

  if (notices.length === 0) return null;

  const notice = notices[current % notices.length];
  if (!notice) return null;

  const tCfg = noticeTypeConfig[notice.type];
  const isCritical = notice.priority === 'critical';
  const isHigh = notice.priority === 'high';
  const messageText = notice.short_message || notice.title;
  const scrollDuration = Math.max(22, messageText.length * 0.35);

  return (
      <div
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        role="region"
        aria-label="Announcements"
        className={`relative overflow-hidden transition-colors backdrop-blur-lg ${
          isCritical
            ? 'bg-gradient-to-r from-red-950/15 via-background/95 to-red-950/15 border-b border-red-900/10'
            : isHigh
            ? 'bg-gradient-to-r from-amber-950/10 via-background/95 to-amber-950/10 border-b border-amber-900/8'
            : 'bg-background/80 border-b border-border/10'
        }`}
      >
        {/* Priority accent — subtle left line */}
        {isCritical && (
          <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-red-500/40 via-red-400/20 to-red-500/40" />
        )}
        {isHigh && (
          <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-amber-500/20 via-amber-400/10 to-amber-500/20" />
        )}

        <div className="container">
          <div className="flex items-center py-2 gap-2 sm:gap-3 text-[11px] sm:text-[11.5px]">

            {/* ─── Left fixed zone: priority icon + type badge ─── */}
            <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
              {isCritical ? (
                <div className="relative flex items-center justify-center">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400/70" />
                  <div className="absolute inset-0 animate-pulse-glow rounded-full">
                    <div className="w-full h-full rounded-full bg-red-500/6" />
                  </div>
                </div>
              ) : (
                <Info className="w-3.5 h-3.5 text-muted-foreground/50" />
              )}

              <span className={`hidden sm:inline text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${tCfg.bg} ${tCfg.border} ${tCfg.color} opacity-80`}>
                {tCfg.label}
              </span>

              {/* Subtle separator — desktop only */}
              <div className="hidden sm:block w-px h-3.5 bg-border/20" />
            </div>

            {/* ─── Center scrolling zone: message text ─── */}
            <div
              ref={containerRef}
              className="relative flex-1 overflow-hidden min-w-0"
            >
              {/* Soft edge fade masks — always present for polish */}
              <div className={`absolute left-0 top-0 bottom-0 w-10 z-10 pointer-events-none ${
                isCritical
                  ? 'bg-gradient-to-r from-red-950/25 to-transparent'
                  : isHigh
                  ? 'bg-gradient-to-r from-amber-950/10 to-transparent'
                  : 'bg-gradient-to-r from-background/80 to-transparent'
              }`} />
              <div className={`absolute right-0 top-0 bottom-0 w-10 z-10 pointer-events-none ${
                isCritical
                  ? 'bg-gradient-to-l from-red-950/25 to-transparent'
                  : isHigh
                  ? 'bg-gradient-to-l from-amber-950/10 to-transparent'
                  : 'bg-gradient-to-l from-background/80 to-transparent'
              }`} />

              <AnimatePresence mode="wait">
                <motion.div
                  key={notice.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="overflow-hidden whitespace-nowrap"
                >
                  <span
                    ref={textRef}
                    className={`font-medium tracking-wide ${
                      needsScroll ? `announcement-scroll ${paused ? 'paused' : ''}` : 'inline-block'
                    } ${
                      isCritical ? 'text-red-200/80' : 'text-foreground/70'
                    }`}
                    style={needsScroll ? { '--scroll-duration': `${scrollDuration}s` } as React.CSSProperties : undefined}
                  >
                    <Link
                      to={`/notices/${notice.id}`}
                      className="hover:underline underline-offset-2"
                    >
                      {messageText}
                    </Link>
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* ─── Right fixed zone: CTA + nav + dismiss ─── */}
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Subtle separator */}
              <div className="w-px h-3.5 bg-border/20 mr-0.5" />

              {/* CTA pill */}
              {notice.cta_label && notice.cta_url && (
                <Link
                  to={notice.cta_url}
                  className={`hidden sm:flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full transition-all border ${
                    isCritical
                      ? 'bg-red-500/10 text-red-300/80 hover:bg-red-500/20 border-red-500/15'
                      : isHigh
                      ? 'bg-amber-500/8 text-amber-300/80 hover:bg-amber-500/15 border-amber-500/12'
                      : 'bg-primary/8 text-primary/80 hover:bg-primary/15 border-primary/15'
                  }`}
                >
                  {notice.cta_label}
                  <ExternalLink className="w-2.5 h-2.5" />
                </Link>
              )}

              {/* Prev / Next */}
              {notices.length > 1 && (
                <div className="hidden sm:flex items-center gap-px ml-0.5">
                  <button
                    onClick={goPrev}
                    className="p-1 rounded hover:bg-secondary/40 transition-colors"
                    aria-label="Previous notice"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 text-muted-foreground/50" />
                  </button>
                  <span className="text-[10px] text-muted-foreground/50 tabular-nums font-medium min-w-[1.5rem] text-center">
                    {(current % notices.length) + 1}/{notices.length}
                  </span>
                  <button
                    onClick={goNext}
                    className="p-1 rounded hover:bg-secondary/40 transition-colors"
                    aria-label="Next notice"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
                  </button>
                </div>
              )}

              {/* Dismiss (non-critical only) */}
              {!isCritical && notice.is_dismissible && (
                <button
                  onClick={() => handleDismiss(notice)}
                  className="p-1 rounded hover:bg-secondary/40 transition-colors ml-0.5"
                  aria-label="Dismiss notice"
                >
                  <X className="w-3 h-3 text-muted-foreground/40" />
                </button>
              )}
            </div>

          </div>
        </div>
      </div>
  );
}
